import type {
  ImportPromptRequest,
  ListPromptsRequest,
  ListPromptsResponse,
  PromptMetadata,
  PromptRecord,
} from './types';

// Keep this at or below the backend prompts.list max limit; larger values fall back to 50.
const MAX_PROMPT_LOOKUP_BATCH_SIZE = 200;

export interface BulkImportPlan {
  toImport: ImportPromptRequest[];
  skipped: string[];
}

type ListPromptsFetcher = (request: ListPromptsRequest) => Promise<ListPromptsResponse>;

// Normalize metadata for comparison (handle null/undefined/empty).
export const normalizeMetadata = (
  meta: PromptMetadata | null | undefined
): PromptMetadata | null => {
  if (!meta) return null;

  const normalized: PromptMetadata = {};
  if (meta.name?.trim()) normalized.name = meta.name.trim();
  if (meta.description?.trim()) normalized.description = meta.description.trim();
  if (meta.category?.trim()) normalized.category = meta.category.trim();
  if (meta.tags && meta.tags.length > 0) {
    normalized.tags = [...meta.tags].sort();
  }

  // Intentionally exclude ui_hints, validation, and extras.
  return Object.keys(normalized).length > 0 ? normalized : null;
};

// Compare two prompts to see if they're identical for bulk import skipping.
export const arePromptsIdentical = (
  existing: PromptRecord,
  imported: ImportPromptRequest
): boolean => {
  if (existing.slug !== imported.slug) return false;

  const existingText = existing.text.trim();
  const importedText = imported.text.trim();
  if (existingText !== importedText) return false;

  const existingMeta = normalizeMetadata(existing.metadata);
  const importedMeta = normalizeMetadata(imported.metadata);

  if (!existingMeta && !importedMeta) return true;
  if (!existingMeta || !importedMeta) return false;

  if (existingMeta.name !== importedMeta.name) return false;
  if (existingMeta.description !== importedMeta.description) return false;
  if (existingMeta.category !== importedMeta.category) return false;

  const existingTags = existingMeta.tags?.sort().join(',') || '';
  const importedTags = importedMeta.tags?.sort().join(',') || '';

  return existingTags === importedTags;
};

export const filterGlobalImportPrompts = (items: ImportPromptRequest[]): ImportPromptRequest[] =>
  items.filter((item) => item.scope.type === 'global');

export type ParsedBulkImport =
  | { ok: true; globalPrompts: ImportPromptRequest[]; filteredCount: number }
  | { ok: false; error: string };

// Parse and validate pasted import JSON, keeping only global-scoped prompts.
export const parseBulkImportText = (text: string): ParsedBulkImport => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (_err) {
    return { ok: false, error: 'Invalid JSON' };
  }

  const items = (Array.isArray(parsed) ? parsed : [parsed]) as ImportPromptRequest[];
  if (!items.length) {
    return { ok: false, error: 'No prompts to import' };
  }

  for (const item of items) {
    if (!item?.slug || !item.scope?.type || !item.text || !item.version) {
      return { ok: false, error: 'Missing required fields (slug, scope, text, version)' };
    }
  }

  const globalPrompts = filterGlobalImportPrompts(items);
  if (globalPrompts.length === 0) {
    return {
      ok: false,
      error:
        'No global-scoped prompts found to import. Org and person scoped prompts are filtered out.',
    };
  }

  return { ok: true, globalPrompts, filteredCount: items.length - globalPrompts.length };
};

export const buildBulkImportPlan = (
  importedGlobalPrompts: ImportPromptRequest[],
  existingGlobalPrompts: PromptRecord[]
): BulkImportPlan => {
  const existingBySlug = new Map<string, PromptRecord>();
  existingGlobalPrompts.forEach((prompt) => {
    existingBySlug.set(prompt.slug, prompt);
  });

  const toImport: ImportPromptRequest[] = [];
  const skipped: string[] = [];

  for (const imported of importedGlobalPrompts) {
    const existing = existingBySlug.get(imported.slug);

    if (existing && arePromptsIdentical(existing, imported)) {
      skipped.push(imported.slug);
      continue;
    }

    toImport.push(imported);
  }

  return { toImport, skipped };
};

export const fetchExistingPublishedGlobalPrompts = async (
  importedGlobalPrompts: ImportPromptRequest[],
  listPrompts: ListPromptsFetcher
): Promise<PromptRecord[]> => {
  const uniqueSlugs = Array.from(new Set(importedGlobalPrompts.map((item) => item.slug)));
  const results: PromptRecord[] = [];

  for (let start = 0; start < uniqueSlugs.length; start += MAX_PROMPT_LOOKUP_BATCH_SIZE) {
    const slugs = uniqueSlugs.slice(start, start + MAX_PROMPT_LOOKUP_BATCH_SIZE);
    if (slugs.length === 0) {
      continue;
    }

    const response = await listPrompts({
      slugs,
      scope_type: 'global',
      status: 'published',
      limit: slugs.length,
    });

    results.push(...response.items);
  }

  return results;
};
